import React, {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { CreateTreeModal } from '../tree-management/CreateTreeModal';
import { LoginModal } from '../auth/LoginModal';
import { MenuInteractor, type MenuAuthViewState } from './MenuInteractor';
import { isEventOutsideElement } from '../ui/outsideClick';

/** Inputs for {@link useMenuDropdown} (routing, location, sign-out). */
interface UseMenuDropdownOptions {
  navigate: NavigateFunction;
  pathname: string;
  signOut: () => Promise<void>;
}

/** Props for {@link MenuDropdownAuthItems}. */
interface MenuDropdownAuthItemsProps {
  authState: MenuAuthViewState;
  header?: React.ReactNode;
  dividerClassName: string;
  itemClassName: string;
  signOutClassName?: string;
  onCreateTree: () => void;
  onMyTrees: () => void;
  onSignIn: () => void;
  onSignOut: () => void | Promise<void>;
}

/** Props for {@link MenuDropdownModals}. */
interface MenuDropdownModalsProps {
  showCreateTreeModal: boolean;
  showLoginModal: boolean;
  onCloseCreateTreeModal: () => void;
  onCloseLoginModal: () => void;
}

/**
 * State and handlers for the header menu: open/close, outside click and Escape, focus return to trigger,
 * Create Tree / Login modals, and ARIA attributes for the trigger and menu panel.
 */
export function useMenuDropdown({
  navigate,
  pathname,
  signOut,
}: UseMenuDropdownOptions) {
  const menuInteractor = useMemo(() => new MenuInteractor(), []);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wasOpenRef = useRef(false);
  const menuPanelId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateTreeModal, setShowCreateTreeModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);

  const menuTriggerProps = useMemo(
    () =>
      ({
        'aria-controls': menuPanelId,
        'aria-expanded': isOpen,
        'aria-haspopup': 'menu' as const,
      }) satisfies React.ButtonHTMLAttributes<HTMLButtonElement>,
    [isOpen, menuPanelId],
  );

  const menuPanelProps = useMemo(
    () =>
      ({
        id: menuPanelId,
        role: 'menu' as const,
      }) satisfies React.HTMLAttributes<HTMLDivElement>,
    [menuPanelId],
  );

  const closeMenu = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleMenu = useCallback(() => {
    setIsOpen((previousValue) => !previousValue);
  }, []);

  const openCreateTreeModal = useCallback(() => {
    setShowCreateTreeModal(true);
    closeMenu();
  }, [closeMenu]);

  const closeCreateTreeModal = useCallback(() => {
    setShowCreateTreeModal(false);
  }, []);

  const openLoginModal = useCallback(() => {
    setShowLoginModal(true);
    closeMenu();
  }, [closeMenu]);

  const closeLoginModal = useCallback(() => {
    setShowLoginModal(false);
  }, []);

  const goToMyTrees = useCallback(() => {
    navigate('/trees');
    closeMenu();
  }, [closeMenu, navigate]);

  const handleSignOut = useCallback(async () => {
    await signOut();
    closeMenu();

    const navigationTarget = menuInteractor.getPostSignOutNavigationTarget(pathname);
    if (navigationTarget) {
      navigate(navigationTarget);
    }
  }, [closeMenu, menuInteractor, navigate, pathname, signOut]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEventOutsideElement(event, menuRef.current)) {
        closeMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [closeMenu]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeMenu();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeMenu, isOpen]);

  useEffect(() => {
    if (wasOpenRef.current && !isOpen) {
      triggerRef.current?.focus();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen]);

  return {
    closeCreateTreeModal,
    closeLoginModal,
    closeMenu,
    goToMyTrees,
    handleSignOut,
    isOpen,
    menuPanelProps,
    menuRef,
    menuTriggerProps,
    openCreateTreeModal,
    openLoginModal,
    setIsOpen,
    showCreateTreeModal,
    showLoginModal,
    toggleMenu,
    triggerRef,
  };
}

/** Auth-dependent menu section: sign-in only, or signed-in actions (create tree, my trees, sign out). */
export function MenuDropdownAuthItems({
  authState,
  header,
  dividerClassName,
  itemClassName,
  signOutClassName,
  onCreateTree,
  onMyTrees,
  onSignIn,
  onSignOut,
}: MenuDropdownAuthItemsProps) {
  if (authState.showSignIn) {
    return (
      <button type="button" className={itemClassName} role="menuitem" onClick={onSignIn}>
        Sign In
      </button>
    );
  }

  const signOutButtonClassName = [itemClassName, signOutClassName].filter(Boolean).join(' ');

  return (
    <>
      {authState.showHeader && header}
      {authState.showHeader && header && (
        <div className={dividerClassName} role="separator" aria-orientation="horizontal" />
      )}
      {authState.showCreateTree && (
        <button type="button" className={itemClassName} role="menuitem" onClick={onCreateTree}>
          Create Tree
        </button>
      )}
      {authState.showMyTrees && (
        <button type="button" className={itemClassName} role="menuitem" onClick={onMyTrees}>
          My Trees
        </button>
      )}
      {authState.showSignOut && (
        <div className={dividerClassName} role="separator" aria-orientation="horizontal" />
      )}
      {authState.showSignOut && (
        <button
          type="button"
          className={signOutButtonClassName}
          role="menuitem"
          onClick={onSignOut}
        >
          Sign Out
        </button>
      )}
    </>
  );
}

/** Mounts Create Tree and Login modals wired to dropdown visibility flags. */
export function MenuDropdownModals({
  showCreateTreeModal,
  showLoginModal,
  onCloseCreateTreeModal,
  onCloseLoginModal,
}: MenuDropdownModalsProps) {
  return (
    <>
      <CreateTreeModal isOpen={showCreateTreeModal} onClose={onCloseCreateTreeModal} />
      <LoginModal isOpen={showLoginModal} onClose={onCloseLoginModal} />
    </>
  );
}
